import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isRxDatabase } from 'rxdb';
import { useIsUserInitialized, useUser } from '@providers/Auth';
import { getNoodleDb, closeCurrentRxNoodleDB, handleRxDBPostLogoutUserProcess } from './rxdb.noodle';
import startReplications from './rxdb.replication';
import { rxdbError, rxdbLog } from './rxdb.tools';

type RxDBContextType = {
  setCreatorId: (creatorId: string | undefined) => void;
};

export const RxDBContext = createContext<RxDBContextType>({
  setCreatorId: () => undefined,
});

const RxDBStorageProvider: React.FC = ({ children }) => {
  const [user] = useUser();
  const [creatorId, setCreatorId] = useState<string | undefined>(undefined);
  const lastCreatorIdUsed = useRef<string | null>(null);
  const lastUserIdUsed = useRef<string | null>(null);
  const isUserInitialized = useIsUserInitialized();
  const stopReplications = useRef<(() => void) | null>(null);

  const initializeDatabase = useCallback(async () => {
    try {
      if (!creatorId || !user?.id) {
        rxdbError('Cannot initialize database - missing creator ID or user ID');
        return;
      }

      // If there is a user and a creatorId set, then a change of creatorId is happening
      // Thus we need to close the current database and cancel any ongoing replications
      if (lastUserIdUsed.current && lastCreatorIdUsed.current) {
        rxdbLog('closing database as we are changing the creatorId', {
          creatorId,
          lastCreatorIdUsed: lastCreatorIdUsed.current,
          lastUserIdUsed: lastUserIdUsed.current,
          userId: user?.id,
        });
        stopReplications.current?.();
        await closeCurrentRxNoodleDB();
      }

      lastCreatorIdUsed.current = creatorId;
      lastUserIdUsed.current = user?.id;

      rxdbLog('initializeDatabase', {
        creatorId,
        lastCreatorIdUsed: lastCreatorIdUsed.current,
        lastUserIdUsed: lastUserIdUsed.current,
        userId: user?.id,
      });

      const db = await getNoodleDb({ creatorId, userId: user?.id });
      const isValidDB = isRxDatabase(db);

      if (!isValidDB) {
        rxdbError('Invalid database instance');
        return;
      }

      rxdbLog('Starting replications');

      stopReplications.current = await startReplications({ creatorId });
    } catch (error) {
      rxdbError('RXDB Provider - An error occurred while initializing the database', error);
    }
  }, [creatorId, user]);

  const resetDatabases = useCallback(() => {
    stopReplications.current?.();
    handleRxDBPostLogoutUserProcess();
    lastCreatorIdUsed.current = null;
    lastUserIdUsed.current = null;

    rxdbLog('resetted databases', {
      creatorId,
      lastCreatorIdUsed: lastCreatorIdUsed.current,
      lastUserIdUsed: lastUserIdUsed.current,
      userId: user?.id,
    });
  }, [creatorId, user]);

  useEffect(() => {
    if (isUserInitialized && lastCreatorIdUsed.current !== creatorId && user?.id && creatorId) {
      rxdbLog('------- Initializing database', {
        creatorId,
        lastCreatorIdUsed: lastCreatorIdUsed.current,
        lastUserIdUsed: lastUserIdUsed.current,
        userId: user?.id,
      });
      initializeDatabase();
    }
  }, [isUserInitialized, creatorId, lastCreatorIdUsed, initializeDatabase, user?.id]);

  useEffect(() => {
    // Reset the databases if the user logs out
    if (lastCreatorIdUsed.current && lastUserIdUsed.current && !creatorId && !user?.id) {
      rxdbLog('------- Resetting databases', {
        creatorId,
        lastCreatorIdUsed: lastCreatorIdUsed.current,
        lastUserIdUsed: lastUserIdUsed.current,
        userId: user?.id,
      });
      resetDatabases();
    }
  }, [lastCreatorIdUsed, lastUserIdUsed, creatorId, user?.id, resetDatabases]);

  const value = useMemo(() => ({
    setCreatorId,
  }), [setCreatorId]);

  return (
    <RxDBContext.Provider value={value}>
      {children}
    </RxDBContext.Provider>
  );
};

export default RxDBStorageProvider;
